import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Plus,
  Trash2,
  Play,
  ArrowRightLeft,
  Shield,
  Users,
  AlertCircle,
  CheckCircle,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { calcROFRTransfer } from '@/utils/equityMath';
import type {
  ROFRShareholder,
  ROFRTransferScenario,
  ROFRTransferResult,
  ROFRTimelineStep,
  ROFRTransferStatus,
} from '@/utils/equityMath';

type Shareholder = ROFRShareholder;

interface TransferScenario extends ROFRTransferScenario {
  id: string;
}

const STATUS_COPY: Record<ROFRTransferStatus, { label: string; detail: string; tone: string }> = {
  completed: {
    label: 'Completed',
    detail: 'the buyer took the whole block',
    tone: 'text-success',
  },
  'partially-preempted': {
    label: 'Partially Preempted',
    detail: 'ROFR holders took part of the block',
    tone: 'text-warning',
  },
  'fully-preempted': {
    label: 'Fully Preempted',
    detail: 'ROFR holders took it all; the buyer got nothing',
    tone: 'text-destructive',
  },
};

const fmtShares = (n: number) => Math.round(n).toLocaleString();
const fmtM = (n: number) => `$${(n / 1000000).toFixed(2)}M`;

function describeStep(step: ROFRTimelineStep): string {
  switch (step.kind) {
    case 'offer':
      return `${step.actor} proposes to sell ${fmtShares(step.shares)} shares at $${step.pricePerShare}/share to ${step.buyer}`;
    case 'rofr-triggered':
      return 'ROFR triggered - offering shares to existing shareholders';
    case 'rofr-bypassed':
      return 'ROFR bypassed (exempt transfer type)';
    case 'rofr-exercised':
      return step.viaCompanyRight
        ? `Priority ${step.priority}: ${step.actor} exercises its company ROFR over the whole block - ${fmtShares(step.shares)} shares (${fmtM(step.amount)})`
        : `Priority ${step.priority}: ${step.actor} exercises ROFR for ${fmtShares(step.shares)} shares (${fmtM(step.amount)})`;
    case 'rofr-declined':
      return `Priority ${step.priority}: ${step.actor} declines ROFR for ${fmtShares(step.shares)} shares`;
    case 'cosale-triggered':
      return `Co-sale rights triggered (${step.salePercentage.toFixed(1)}% of seller's holdings) - the block is now shared between the seller and the tag-along holders`;
    case 'cosale-sold':
      return `Co-sale: ${step.actor} sells ${fmtShares(step.shares)} shares (${fmtM(step.amount)}) out of the seller's allocation`;
    case 'third-party-purchase':
      return `${step.buyer} purchases ${fmtShares(step.shares)} shares (${fmtM(step.amount)})`;
    case 'outcome':
      return `${STATUS_COPY[step.status].label} - ${STATUS_COPY[step.status].detail}`;
  }
}

export default function ROFRSimulator() {
  const [shareholders, setShareholders] = useState<Shareholder[]>([
    {
      id: '1',
      name: 'Company',
      shares: 0,
      hasROFR: true,
      rofrPriority: 1,
      hasCoSaleRight: false,
      isCompany: true,
    },
    {
      id: '2',
      name: 'Series A Lead',
      shares: 3000000,
      hasROFR: true,
      rofrPriority: 2,
      hasCoSaleRight: false,
      isCompany: false,
    },
    {
      id: '3',
      name: 'Founder 1',
      shares: 2500000,
      hasROFR: false,
      rofrPriority: 99,
      hasCoSaleRight: true,
      isCompany: false,
    },
    {
      id: '4',
      name: 'Founder 2',
      shares: 2000000,
      hasROFR: false,
      rofrPriority: 99,
      hasCoSaleRight: true,
      isCompany: false,
    },
    {
      id: '5',
      name: 'Angel Investors',
      shares: 1500000,
      hasROFR: false,
      rofrPriority: 99,
      hasCoSaleRight: false,
      isCompany: false,
    },
    {
      id: '6',
      name: 'Employee Pool',
      shares: 1000000,
      hasROFR: false,
      rofrPriority: 99,
      hasCoSaleRight: false,
      isCompany: false,
    },
  ]);

  const [rofrExercisePct, setRofrExercisePct] = useState(100); // % of ROFR rights holders actually exercise
  const [coSaleTriggerPct, setCoSaleTriggerPct] = useState(10); // Min % of shares being sold to trigger co-sale

  const [transferScenarios, setTransferScenarios] = useState<TransferScenario[]>([
    {
      id: '1',
      name: 'Founder Exit',
      sellerId: '3',
      sharesForSale: 500000,
      pricePerShare: 5,
      thirdPartyBuyerName: 'External Investor',
      bypassROFR: false,
    },
    {
      id: '2',
      name: 'Secondary Sale',
      sellerId: '5',
      sharesForSale: 750000,
      pricePerShare: 6,
      thirdPartyBuyerName: 'PE Fund',
      bypassROFR: false,
    },
    {
      id: '3',
      name: 'Estate Transfer',
      sellerId: '4',
      sharesForSale: 200000,
      pricePerShare: 5,
      thirdPartyBuyerName: 'Family Trust',
      bypassROFR: true,
    },
  ]);

  const [results, setResults] = useState<ROFRTransferResult[]>([]);
  const [analyzing, setAnalyzing] = useState(false);

  const addShareholder = () => {
    const newId = String(Date.now());
    setShareholders([
      ...shareholders,
      {
        id: newId,
        name: 'New Shareholder',
        shares: 0,
        hasROFR: false,
        rofrPriority: 99,
        hasCoSaleRight: false,
        isCompany: false,
      },
    ]);
  };

  const updateShareholder = (
    id: string,
    field: keyof Shareholder,
    value: string | number | boolean
  ) => {
    setShareholders(shareholders.map((sh) => (sh.id === id ? { ...sh, [field]: value } : sh)));
  };

  const removeShareholder = (id: string) => {
    setShareholders(shareholders.filter((sh) => sh.id !== id));
  };

  const addScenario = () => {
    const newId = String(Date.now());
    setTransferScenarios([
      ...transferScenarios,
      {
        id: newId,
        name: 'New Transfer',
        sellerId: shareholders[0]?.id || '',
        sharesForSale: 0,
        pricePerShare: 0,
        thirdPartyBuyerName: '',
        bypassROFR: false,
      },
    ]);
  };

  const updateScenario = (
    id: string,
    field: keyof TransferScenario,
    value: string | number | boolean
  ) => {
    setTransferScenarios(
      transferScenarios.map((sc) => (sc.id === id ? { ...sc, [field]: value } : sc))
    );
  };

  const removeScenario = (id: string) => {
    setTransferScenarios(transferScenarios.filter((sc) => sc.id !== id));
  };

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => {
      setResults(
        transferScenarios.map((scenario) =>
          calcROFRTransfer(scenario, shareholders, { rofrExercisePct, coSaleTriggerPct })
        )
      );
      setAnalyzing(false);
    }, 800);
  };

  // Chart data
  const shareholderData = shareholders
    .filter((sh) => sh.shares > 0)
    .map((sh) => ({
      name: sh.name,
      shares: sh.shares,
      hasROFR: sh.hasROFR,
      hasCoSale: sh.hasCoSaleRight,
      priority: sh.rofrPriority,
    }));

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  const totalShares = shareholders.reduce((sum, sh) => sum + sh.shares, 0);
  const rofrHoldersCount = shareholders.filter((sh) => sh.hasROFR).length;
  const coSaleHoldersCount = shareholders.filter((sh) => sh.hasCoSaleRight).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            ROFR & Co-Sale Agreement Simulator
          </CardTitle>
          <CardDescription>
            Model secondary transfer scenarios with rights of first refusal and co-sale mechanics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Configuration */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>ROFR Exercise Rate (%)</Label>
              <Input
                type="number"
                value={rofrExercisePct}
                onChange={(e) => setRofrExercisePct(Number(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                % of ROFR holders who exercise. The holders with the highest priority are the ones
                who do, so the same inputs always give the same result.
              </p>
            </div>
            <div>
              <Label>Co-Sale Trigger Threshold (%)</Label>
              <Input
                type="number"
                value={coSaleTriggerPct}
                onChange={(e) => setCoSaleTriggerPct(Number(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum % of seller's holdings to trigger co-sale rights
              </p>
            </div>
          </div>

          {/* Shareholders */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Shareholders & Rights</h3>
              <Button onClick={addShareholder} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Shareholder
              </Button>
            </div>
            <div className="space-y-2">
              {shareholders.map((sh) => (
                <div key={sh.id} className="p-3 border rounded-lg">
                  <div className="grid grid-cols-12 gap-2 items-center mb-2">
                    <div className="col-span-3">
                      <Input
                        placeholder="Name"
                        value={sh.name}
                        onChange={(e) => updateShareholder(sh.id, 'name', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        placeholder="Shares"
                        value={sh.shares}
                        onChange={(e) => updateShareholder(sh.id, 'shares', Number(e.target.value))}
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <div className="px-3 py-2 bg-muted rounded-md text-sm">
                        {(totalShares > 0 ? (sh.shares / totalShares) * 100 : 0).toFixed(1)}%
                      </div>
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        placeholder="Priority"
                        value={sh.rofrPriority}
                        onChange={(e) =>
                          updateShareholder(sh.id, 'rofrPriority', Number(e.target.value))
                        }
                        disabled={!sh.hasROFR}
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2 flex gap-1">
                      {sh.hasROFR && <Badge variant="secondary">ROFR</Badge>}
                      {sh.hasCoSaleRight && <Badge variant="outline">Co-Sale</Badge>}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button onClick={() => removeShareholder(sh.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.hasROFR}
                        onCheckedChange={(checked) => updateShareholder(sh.id, 'hasROFR', checked)}
                        id={`rofr-${sh.id}`}
                      />
                      <Label htmlFor={`rofr-${sh.id}`} className="text-xs cursor-pointer">
                        ROFR Rights
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.hasCoSaleRight}
                        onCheckedChange={(checked) =>
                          updateShareholder(sh.id, 'hasCoSaleRight', checked)
                        }
                        id={`cosale-${sh.id}`}
                      />
                      <Label htmlFor={`cosale-${sh.id}`} className="text-xs cursor-pointer">
                        Co-Sale Rights
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.isCompany}
                        onCheckedChange={(checked) =>
                          updateShareholder(sh.id, 'isCompany', checked)
                        }
                        id={`company-${sh.id}`}
                      />
                      <Label htmlFor={`company-${sh.id}`} className="text-xs cursor-pointer">
                        Is Company
                      </Label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-muted/50 rounded-lg grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Shares:</span>
                <span className="ml-2 font-bold">{totalShares.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">ROFR Holders:</span>
                <span className="ml-2 font-bold">{rofrHoldersCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Co-Sale Holders:</span>
                <span className="ml-2 font-bold">{coSaleHoldersCount}</span>
              </div>
            </div>
          </div>

          {/* Transfer Scenarios */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Transfer Scenarios</h3>
              <Button onClick={addScenario} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Scenario
              </Button>
            </div>
            <div className="space-y-3">
              {transferScenarios.map((scenario) => (
                <div key={scenario.id} className="p-3 border rounded-lg">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-2">
                      <Label className="text-xs">Scenario Name</Label>
                      <Input
                        value={scenario.name}
                        onChange={(e) => updateScenario(scenario.id, 'name', e.target.value)}
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Seller</Label>
                      <select
                        value={scenario.sellerId}
                        onChange={(e) => updateScenario(scenario.id, 'sellerId', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                      >
                        {shareholders
                          .filter((sh) => sh.shares > 0)
                          .map((sh) => (
                            <option key={sh.id} value={sh.id}>
                              {sh.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Shares for Sale</Label>
                      <Input
                        type="number"
                        value={scenario.sharesForSale}
                        onChange={(e) =>
                          updateScenario(scenario.id, 'sharesForSale', Number(e.target.value))
                        }
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Price/Share ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={scenario.pricePerShare}
                        onChange={(e) =>
                          updateScenario(scenario.id, 'pricePerShare', Number(e.target.value))
                        }
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Third-Party Buyer</Label>
                      <Input
                        value={scenario.thirdPartyBuyerName}
                        onChange={(e) =>
                          updateScenario(scenario.id, 'thirdPartyBuyerName', e.target.value)
                        }
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div className="col-span-1 flex items-end pb-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={scenario.bypassROFR}
                          onCheckedChange={(checked) =>
                            updateScenario(scenario.id, 'bypassROFR', checked)
                          }
                          id={`bypass-${scenario.id}`}
                        />
                        <Label
                          htmlFor={`bypass-${scenario.id}`}
                          className="text-xs cursor-pointer whitespace-nowrap"
                        >
                          Bypass
                        </Label>
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end items-end pb-2">
                      <Button onClick={() => removeScenario(scenario.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Rights Explained:</strong> ROFR gives existing shareholders the right to
              purchase shares before they're sold to third parties, offered by priority (1 = first).
              A holder marked <strong>Is Company</strong> has a right over the whole offered block;
              everyone else at a priority level is offered a pro-rata slice, and anything declined
              falls through to the next level. Co-sale rights let other shareholders take part of
              the buyer's block, which reduces what the seller sells rather than adding to the deal.
            </AlertDescription>
          </Alert>

          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            <Play className="h-4 w-4 mr-2" />
            {analyzing ? 'Analyzing...' : 'Simulate Transfer Scenarios'}
          </Button>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Transfer Simulation Results</CardTitle>
              <CardDescription>
                Detailed breakdown of ROFR allocation, co-sale participation, and transfer mechanics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="0">
                <TabsList
                  className="grid w-full"
                  style={{ gridTemplateColumns: `repeat(${results.length}, 1fr)` }}
                >
                  {results.map((result, idx) => (
                    <TabsTrigger key={idx} value={String(idx)}>
                      {result.scenarioName}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {results.map((result, idx) => (
                  <TabsContent key={idx} value={String(idx)} className="space-y-4">
                    {result.warnings.map((warning, i) => (
                      <Alert key={i} variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">{warning}</AlertDescription>
                      </Alert>
                    ))}

                    <div className="grid md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Seller</span>
                          </div>
                          <p className="text-lg font-bold">{result.sellerName}</p>
                          <p className="text-sm text-muted-foreground">
                            {result.sharesForSale.toLocaleString()} shares
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Total Value</span>
                          </div>
                          <p className="text-lg font-bold">
                            ${(result.totalValue / 1000000).toFixed(2)}M
                          </p>
                          <p className="text-sm text-muted-foreground">
                            ${result.pricePerShare}/share
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">ROFR Status</span>
                          </div>
                          <p
                            className={`text-lg font-bold ${result.rofrTriggered ? 'text-primary' : 'text-muted-foreground'}`}
                          >
                            {result.rofrTriggered ? 'Triggered' : 'Bypassed'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {fmtShares(result.sharesPreemptedByROFR)} preempted
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            {result.status === 'completed' ? (
                              <CheckCircle className="h-4 w-4 text-success" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            )}
                            <span className="text-sm text-muted-foreground">Transfer Status</span>
                          </div>
                          <p className={`text-lg font-bold ${STATUS_COPY[result.status].tone}`}>
                            {STATUS_COPY[result.status].label}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {fmtShares(result.thirdPartyPurchase)} of{' '}
                            {fmtShares(result.sharesForSale)} to {result.thirdPartyBuyerName}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {result.rofrTriggered && result.rofrAllocations.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">ROFR Allocation (by Priority)</h4>
                        <div className="rounded-lg border overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-semibold">
                                  Shareholder
                                </th>
                                <th className="px-4 py-2 text-center text-sm font-semibold">
                                  Priority
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Pro-Rata Entitlement
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Shares Offered
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Shares Accepted
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Amount Paid
                                </th>
                                <th className="px-4 py-2 text-center text-sm font-semibold">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {result.rofrAllocations.map((alloc, i) => (
                                <tr key={i} className="hover:bg-muted/30">
                                  <td className="px-4 py-3 font-medium">{alloc.shareholderName}</td>
                                  <td className="px-4 py-3 text-center">
                                    <Badge variant="outline">{alloc.priority}</Badge>
                                  </td>
                                  <td className="px-4 py-3 text-right text-sm">
                                    {alloc.proRataEntitlement.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right text-sm">
                                    {alloc.sharesOffered.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    {alloc.sharesAccepted.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right font-bold">
                                    ${(alloc.amountPaid / 1000000).toFixed(2)}M
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {alloc.exercisesRight ? (
                                      <CheckCircle className="h-4 w-4 text-success inline" />
                                    ) : (
                                      <Badge variant="secondary">Declined</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {result.coSaleTriggered && result.coSaleParticipants.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Co-Sale Participation</h4>
                        <div className="rounded-lg border overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-semibold">
                                  Shareholder
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Max Shares Can Sell
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Shares Sold
                                </th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">
                                  Proceeds
                                </th>
                                <th className="px-4 py-2 text-center text-sm font-semibold">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {result.coSaleParticipants.map((participant, i) => (
                                <tr key={i} className="hover:bg-muted/30">
                                  <td className="px-4 py-3 font-medium">
                                    {participant.shareholderName}
                                  </td>
                                  <td className="px-4 py-3 text-right text-sm">
                                    {participant.maxSharesCanSell.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    {participant.sharesSold.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right font-bold">
                                    ${(participant.proceeds / 1000000).toFixed(2)}M
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {participant.participates ? (
                                      <CheckCircle className="h-4 w-4 text-success inline" />
                                    ) : (
                                      <Badge variant="secondary">No Shares</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="font-semibold mb-3">Proceeds Distribution</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={result.proceedsDistribution}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="recipient" />
                          <YAxis
                            label={{ value: 'Proceeds ($M)', angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip
                            formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                          />
                          <Legend />
                          <Bar dataKey="proceeds" fill="hsl(var(--chart-1))" name="Proceeds ($)">
                            {result.proceedsDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3">Transfer Timeline</h4>
                      <div className="space-y-2">
                        {result.timeline.map((event, i) => (
                          <Alert
                            key={i}
                            className={i === result.timeline.length - 1 ? 'border-primary' : ''}
                          >
                            <AlertDescription className="text-sm">
                              <span className="font-semibold text-muted-foreground">
                                Step {i + 1}:
                              </span>{' '}
                              {describeStep(event)}
                            </AlertDescription>
                          </Alert>
                        ))}
                      </div>
                    </div>

                    {result.thirdPartyPurchase > 0 && (
                      <Alert>
                        <Users className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Third-Party Purchase:</strong> {result.thirdPartyBuyerName}{' '}
                          purchased {fmtShares(result.thirdPartyPurchase)} shares for{' '}
                          {fmtM(result.thirdPartyPurchase * result.pricePerShare)}
                          {result.coSaleTriggered && (
                            <>
                              {' '}
                              — {fmtShares(result.sellerSharesToThirdParty)} from{' '}
                              {result.sellerName} and{' '}
                              {fmtShares(
                                result.thirdPartyPurchase - result.sellerSharesToThirdParty
                              )}{' '}
                              from co-sale holders.
                            </>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
